import { z } from 'zod';
import { ValidationError } from './errors.js';
import { getDTFRate, round2 } from './helpers.js';
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
export const QuoteDtfTransferInputSchema = z.object({
    lengthIn: z.number().positive().max(1200),
    qty: z.number().int().positive(),
    customerType: z.enum(['b2c', 'b2b', 'etsy']),
});
export const QuoteDtfTransferResultSchema = z
    .object({
    qty: z.number().int(),
    sqInPerTransfer: z.number(),
    totalSqIn: z.number(),
    ratePerSqIn: z.number(),
    internalCostPerTransfer: z.number(),
    rawSellPerTransfer: z.number(),
    sellPerTransfer: z.number(),
    floorPerTransfer: z.number(),
    flooredAt: z.boolean(),
    orderCost: z.number(),
    orderTotal: z.number(),
    profit: z.number(),
    marginPct: z.number(),
})
    .meta({
    id: 'QuoteDtfTransferResult',
    description: 'DTF Transfer quote output. `flooredAt: true` means rawSellPerTransfer was raised up to the minimum-ticket floor.',
});
/**
 * DTF Transfer: customer buys transfer sheets to press themselves. Fixed 35" width,
 * variable length. Rates tiered by length × customer type (see `getDTFRate`).
 *
 * Floor semantics (per CLAUDE.md § 1.1): DTF's per-transfer floor ($8 B2B / $10
 * B2C+Etsy) is a minimum ticket size, not a below-cost guardrail. The engine raises
 * sell up to the floor and sets `flooredAt: true` — it does NOT throw. SP uses a
 * different rule.
 */
export function quoteDtfTransfer(rawInput, config) {
    const parsed = QuoteDtfTransferInputSchema.safeParse(rawInput);
    if (!parsed.success) {
        throw new ValidationError(parsed.error.issues);
    }
    const { lengthIn, qty, customerType } = parsed.data;
    const dtf = (config ?? DEFAULT_PRICING_CONFIG_BODY).dtfTransfer;
    const sqInPerTransfer = dtf.sheetWidthIn * lengthIn;
    const totalSqIn = sqInPerTransfer * qty;
    const ratePerSqIn = getDTFRate(lengthIn, customerType, dtf);
    const internalCostPerTransfer = sqInPerTransfer * dtf.internalCostPerSqIn;
    const rawSellPerTransfer = sqInPerTransfer * ratePerSqIn;
    const floorPerTransfer = dtf.floor[customerType];
    const flooredAt = rawSellPerTransfer < floorPerTransfer;
    const sellPerTransfer = flooredAt ? floorPerTransfer : rawSellPerTransfer;
    const orderCost = internalCostPerTransfer * qty;
    const orderTotal = sellPerTransfer * qty;
    const profit = orderTotal - orderCost;
    const marginPct = orderTotal > 0 ? (profit / orderTotal) * 100 : 0;
    return {
        qty,
        sqInPerTransfer,
        totalSqIn,
        ratePerSqIn,
        internalCostPerTransfer: round2(internalCostPerTransfer),
        rawSellPerTransfer: round2(rawSellPerTransfer),
        sellPerTransfer: round2(sellPerTransfer),
        floorPerTransfer,
        flooredAt,
        orderCost: round2(orderCost),
        orderTotal: round2(orderTotal),
        profit: round2(profit),
        marginPct: round2(marginPct),
    };
}
//# sourceMappingURL=quote-dtf-transfer.js.map